import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Modal from '../../../components/orizon-ui/Modal';
import StatusPill from '../../../components/orizon-ui/StatusPill';
import { FormSection, InfoBox } from '../../../components/orizon-ui/FormField';
import { useToast } from '../../../components/orizon-ui/Toast';
import { useConfirm } from '../../../components/orizon-ui/ConfirmDialog';
import { formattaQta, formattaDataOra, formattaData } from '../../../components/gru/scorta';
import { CaricoModal } from '../../../components/gru/AzioniGiacenza';
import {
  gruArticolo, gruAnnullaIntervento, gruSegnaOrdineRicevuto, nuovaChiaveIdempotenza, mapGruError,
} from '../../../lib/services/gruService';
import FormIntervento from './FormIntervento';

export const tonoTipo = (r) => {
  if (r.tipo_movimento === 'sostituzione') return 'error';
  if (r.tipo_movimento === 'rientrato') return 'ok';
  return 'warn';
};

export const tonoStato = (stato) => ({
  registrato: 'ok', ordinato: 'warn', ricevuto: 'info', annullato: 'neutro',
}[stato] ?? 'neutro');

export const etichettaStato = (stato) => ({
  registrato: 'Registrato', ordinato: 'In ordine', ricevuto: 'Ricevuto', annullato: 'Annullato',
}[stato] ?? stato);

export const descrizioneComponente = (r) => {
  if (!r.componente_id) return null;
  return `${r.reparto_label ?? ''}${r.componente_posizione ? ` ${r.componente_posizione}` : ''}${r.componente_etichetta ? ` — ${r.componente_etichetta}` : ''}`.trim();
};

const Campo = ({ label, valore, mono = false }) => (
  <div className="flex flex-col gap-1.5 min-w-0">
    <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none truncate">{label}</span>
    <span className={`text-[15px] font-bold text-foreground/90 break-words ${mono ? 'font-mono tracking-tight' : ''}`}>
      {valore === undefined || valore === null || valore === '' ? '---' : valore}
    </span>
  </div>
);

const Bottone = ({ stile, icona, label, onClick, disabilitato }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabilitato}
    className={`flex items-center gap-2 px-4 py-3 rounded-2xl border text-[11px] font-black uppercase tracking-widest transition-all disabled:opacity-50 ${stile}`}
  >
    <Icon name={icona} size={14} />
    {label}
  </button>
);

/**
 * Dettaglio di un intervento.
 *  - Modifica: stessa forma del nuovo intervento; il server decide se basta aggiornare la riga o se serve
 *    stornare il movimento e registrarne uno nuovo (una sola transazione).
 *  - Elimina: annulla l'intervento (la giacenza torna com'era con un movimento di storno). Nulla viene cancellato.
 *  - Ordine aperto: Registra ricezione.
 * In fondo: chi ha registrato l'intervento e chi lo ha modificato per ultimo.
 */
const DettaglioIntervento = ({ intervento, onClose, onAggiornato }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const [sottoModale, setSottoModale] = useState(null); // 'modifica' | 'ricezione'
  const [articolo, setArticolo] = useState(null);
  const [occupato, setOccupato] = useState(false);

  const dec = Number(intervento.unita_decimali ?? 0);
  const aperto = intervento.stato === 'ordinato';
  const annullato = intervento.stato === 'annullato';
  const ricevuto = intervento.stato === 'ricevuto';
  const eOrdine = intervento.tipo_movimento === null;

  // quantità dell'ordine: ordinata, ricevuta (somma dei rientri / carichi collegati) e residua (mai negativa)
  const qtaRicevuta = Number(intervento.qta_ricevuta ?? 0);
  const qtaResidua = Number(intervento.qta_residua ?? 0);

  const segnaRicevuto = async () => {
    const ok = await confirm({
      titolo: 'Segnare l\'ordine come ricevuto?',
      descrizione: `L'ordine di "${intervento.nome}" verrà chiuso senza nuovi movimenti di magazzino: la giacenza non cambia. Usalo quando la merce è già stata caricata in altro modo. Residuo attuale: ${formattaQta(qtaResidua, dec)} ${intervento.unita_label}.`,
      confermaLabel: 'Segna come ricevuto',
      variante: 'primary',
    });
    if (!ok) return;

    setOccupato(true);
    try {
      await gruSegnaOrdineRicevuto({ interventoId: intervento.id });
      toast.success('Ordine segnato come ricevuto.');
      onAggiornato();
      onClose();
    } catch (e) {
      toast.error(mapGruError(e).message);
    } finally {
      setOccupato(false);
    }
  };

  const apriRicezione = async () => {
    setOccupato(true);
    try {
      const a = await gruArticolo(intervento.articolo_id);
      if (!a) {
        toast.error('Articolo non trovato.');
        return;
      }
      setArticolo(a);
      setSottoModale('ricezione');
    } catch (e) {
      toast.error(mapGruError(e).message);
    } finally {
      setOccupato(false);
    }
  };

  const elimina = async () => {
    const ok = await confirm({
      titolo: eOrdine ? 'Eliminare l\'ordine?' : 'Eliminare l\'intervento?',
      descrizione: eOrdine
        ? `L'ordine di ${formattaQta(intervento.qta, dec)} ${intervento.unita_label} "${intervento.nome}" verrà annullato. La giacenza non cambia.`
        : `La giacenza di "${intervento.nome}" torna com'era prima, con un movimento di storno. L'intervento resta visibile come Annullato e nello Storico rimane la traccia.`,
      confermaLabel: 'Elimina',
    });
    if (!ok) return;

    setOccupato(true);
    try {
      await gruAnnullaIntervento({ chiave: nuovaChiaveIdempotenza(), interventoId: intervento.id });
      toast.success(eOrdine ? 'Ordine eliminato.' : 'Intervento eliminato: giacenza ripristinata.');
      onAggiornato();
      onClose();
    } catch (e) {
      toast.error(mapGruError(e).message);
    } finally {
      setOccupato(false);
    }
  };

  const fatto = () => {
    setSottoModale(null);
    onAggiornato();
    onClose();
  };

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        titolo={intervento.nome}
        etichetta={`Intervento · ${intervento.tipo_label}`}
        larghezza="lg"
        bloccaChiusura={sottoModale !== null || occupato}
        footer={(
          <div className="w-full flex flex-col gap-4">
            {!annullato && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {aperto && (
                  <Bottone
                    stile="bg-emerald-500/10 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500 hover:text-white"
                    icona="PackagePlus" label="Registra ricezione" onClick={apriRicezione} disabilitato={occupato}
                  />
                )}
                {aperto && (
                  <Bottone
                    stile="bg-muted text-foreground border-border/40 hover:bg-muted/80"
                    icona="CheckCheck" label="Segna come ricevuto" onClick={segnaRicevuto} disabilitato={occupato}
                  />
                )}
                <Bottone
                  stile="bg-primary text-primary-foreground border-primary hover:opacity-90 shadow-lg shadow-primary/20"
                  icona="Pencil" label="Modifica" onClick={() => setSottoModale('modifica')} disabilitato={occupato}
                />
                {!ricevuto && (
                  <Bottone
                    stile="bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500 hover:text-white"
                    icona="Trash2" label="Elimina" onClick={elimina} disabilitato={occupato}
                  />
                )}
              </div>
            )}

            {/* Chi ha registrato / modificato: in fondo, separato dal resto */}
            <div className="w-full flex flex-wrap items-center justify-between gap-x-6 gap-y-1 pt-3 border-t border-border/30 text-[10px] font-black uppercase tracking-widest text-muted-foreground/50">
              <span className="flex items-center gap-1.5">
                <Icon name="UserRound" size={12} />
                Registrato da <span className="text-foreground/70">{intervento.utente_nome}</span>
                {intervento.created_at && <span className="font-mono normal-case tracking-normal">· {formattaDataOra(intervento.created_at)}</span>}
              </span>
              {intervento.modificato_da_nome && (
                <span className="flex items-center gap-1.5">
                  <Icon name="PenLine" size={12} />
                  Ultima modifica <span className="text-foreground/70">{intervento.modificato_da_nome}</span>
                  {intervento.modificato_il && <span className="font-mono normal-case tracking-normal">· {formattaDataOra(intervento.modificato_il)}</span>}
                </span>
              )}
            </div>
          </div>
        )}
      >
        <div className="flex flex-wrap items-center gap-3">
          <StatusPill tono={tonoTipo(intervento)} label={intervento.tipo_label} />
          <StatusPill tono={tonoStato(intervento.stato)} label={etichettaStato(intervento.stato)} pulse={aperto} />
          {intervento.correzione_di && <StatusPill tono="info" label="Corretto" />}
        </div>

        {annullato && (
          <InfoBox tono="warn">
            Intervento annullato: la giacenza è stata ripristinata. Resta visibile per tenere la traccia.
          </InfoBox>
        )}
        {intervento.correzione_di && !annullato && (
          <InfoBox>Questo intervento sostituisce uno precedente, corretto e annullato.</InfoBox>
        )}

        <FormSection titolo="Intervento" icona="Wrench">
          <div className="grid grid-cols-2 gap-y-8 gap-x-4">
            <Campo label="Data" valore={formattaDataOra(intervento.data_intervento)} />
            <Campo label="Gru" valore={intervento.gru_codice} mono />
            <Campo label="Componente" valore={descrizioneComponente(intervento)} />
            <Campo label="Causa" valore={intervento.causa_label} />
          </div>
        </FormSection>

        <FormSection titolo="Articolo" icona="Package">
          <div className="grid grid-cols-2 gap-y-8 gap-x-4">
            <Campo label="Articolo" valore={intervento.nome} />
            <Campo label="Codice" valore={intervento.ean} mono />
            <Campo label={eOrdine ? 'Quantità ordinata' : 'Quantità movimentata'} valore={`${formattaQta(intervento.qta, dec)} ${intervento.unita_label}`} />
          </div>
        </FormSection>

        {eOrdine && (
          <FormSection titolo="Ordine" icona="ClipboardList">
            <div className="grid grid-cols-2 gap-y-8 gap-x-4">
              <Campo label="Ordinati" valore={`${formattaQta(intervento.qta, dec)} ${intervento.unita_label}`} />
              <Campo label="Ricevuti" valore={`${formattaQta(qtaRicevuta, dec)} ${intervento.unita_label}${intervento.n_ricezioni ? ` (${intervento.n_ricezioni} ${intervento.n_ricezioni === 1 ? 'ricezione' : 'ricezioni'})` : ''}`} />
              <Campo label="Residuo" valore={`${formattaQta(qtaResidua, dec)} ${intervento.unita_label}`} />
              <Campo label="Data ordine" valore={formattaData(intervento.data_ordine)} />
              <Campo label="Consegna prevista" valore={formattaData(intervento.data_consegna_prevista)} />
              <Campo label="Ricevuto il" valore={intervento.data_ricezione ? formattaData(intervento.data_ricezione) : null} />
            </div>
          </FormSection>
        )}

        <FormSection titolo="Note" icona="FileText">
          {intervento.note
            ? <p className="text-sm font-medium text-foreground/70 leading-relaxed italic">"{intervento.note}"</p>
            : <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40 italic">Nessuna nota presente.</p>}
        </FormSection>
      </Modal>

      {sottoModale === 'modifica' && (
        <FormIntervento
          intervento={intervento}
          onClose={() => setSottoModale(null)}
          onSaved={fatto}
        />
      )}

      {sottoModale === 'ricezione' && articolo && (
        <CaricoModal
          articolo={articolo}
          ordineId={intervento.id}
          onClose={() => setSottoModale(null)}
          onDone={fatto}
        />
      )}
    </>
  );
};

export default DettaglioIntervento;
